import React, { useState, useEffect, useRef } from 'react';

const AnimatedCard = ({ children, style, delay = 0, disableAnimation = false }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (disableAnimation) {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [disableAnimation]);

  return (
    <div
      ref={ref}
      className="widget-card"
      style={{
        ...style,
        opacity: disableAnimation ? 1 : (isVisible ? 1 : 0),
        animation: disableAnimation ? 'none' : (isVisible ? `pop-up 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) ${delay}s both` : 'none')
      }}
    >
      {children}
    </div>
  );
};

function Privacy({ darkMode }) {
  const textColor = darkMode ? '#e5e5e5' : '#4b4b4b';
  const headingColor = darkMode ? '#fff' : '#3c3c3c';

  const getCardStyle = (isHeader = false) => ({
    padding: isHeader ? '15px 20px' : '20px',
    backgroundColor: darkMode ? 'rgba(43, 43, 43, 0.05)' : 'rgba(255, 255, 255, 0.03)',
    backdropFilter: 'blur(5px)',
    WebkitBackdropFilter: 'blur(5px)',
    border: darkMode ? '2px solid #444' : '2px solid #e5e5e5',
    borderRadius: '16px',
    boxShadow: '0 4px 0 rgba(0,0,0,0.05)',
    marginBottom: '10px'
  });

  return (
    <main className="main-content" style={{ height: '100vh', overflowY: 'auto', paddingBottom: '40px' }}>
      <style>{`
        @keyframes pop-up {
          0% { transform: scale(0.95); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
      <div className="main-content-header" style={{ position: 'sticky', top: '20px', zIndex: 10, backgroundColor: '#1cb0f6', borderBottom: '4px solid #148ac2', borderRadius: '16px', color: 'white', marginBottom: '20px', marginTop: '20px' }}>
        <h2>Datenschutz</h2>
        <span>Deine Privatsphäre</span>
      </div>

      <div style={{ padding: '20px', maxWidth: '700px', margin: '0 auto', color: textColor }}>
        <AnimatedCard style={getCardStyle(true)} delay={0}>
            <h1 style={{ margin: 0, color: headingColor, fontSize: '1.5rem' }}>Datenschutzerklärung</h1>
        </AnimatedCard>

        <AnimatedCard style={getCardStyle()} delay={0.1}>
            <h2 style={{ color: headingColor, fontSize: '1.1rem', marginTop: 0 }}>1. Datenschutz auf einen Blick</h2>
            <p style={{ fontSize: '0.9rem', lineHeight: '1.4', margin: 0 }}>
              <strong>Allgemeine Hinweise</strong><br />
              Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können.
              <br/><br/>
              <strong>Datenerfassung auf dieser Website</strong><br/>
              Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen Kontaktdaten können Sie dem Impressum dieser Website entnehmen.
              Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen (z.B. durch Registrierung). Andere Daten werden automatisch beim Besuch der Website durch unsere IT-Systeme erfasst (z.B. technische Daten wie Internetbrowser, Betriebssystem oder Uhrzeit des Seitenaufrufs).
            </p>
        </AnimatedCard>

        <AnimatedCard style={getCardStyle()} delay={0.2}>
            <h2 style={{ color: headingColor, fontSize: '1.1rem', marginTop: 0 }}>2. Hosting</h2>
            <p style={{ fontSize: '0.9rem', lineHeight: '1.4', margin: 0 }}>
              Wir hosten die Inhalte unserer Website bei folgendem Anbieter:
            </p>
            <p style={{ fontSize: '0.9rem', lineHeight: '1.4', marginTop: '8px', marginBottom: 0 }}>
              <strong>Firebase Hosting</strong><br/>
              Anbieter ist die Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland (nachfolgend „Google“).<br/>
              Firebase Hosting ist ein Content Delivery Network (CDN), das dazu dient, die Ladezeiten unserer Website zu optimieren. Hierbei werden Anfragen an Server von Google weitergeleitet.
              Die Nutzung erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO. Wir haben ein berechtigtes Interesse an einer möglichst zuverlässigen Darstellung unserer Website.
            </p>
        </AnimatedCard>

        <AnimatedCard style={getCardStyle()} delay={0.3}>
            <h2 style={{ color: headingColor, fontSize: '1.1rem', marginTop: 0 }}>3. Allgemeine Hinweise und Pflichtinformationen</h2>
            <p style={{ fontSize: '0.9rem', lineHeight: '1.4', margin: 0 }}>
              <strong>Datenschutz</strong><br/>
              Die Betreiber dieser Seiten nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Wir behandeln Ihre personenbezogenen Daten vertraulich und entsprechend der gesetzlichen Datenschutzvorschriften sowie dieser Datenschutzerklärung.
            </p>
            <p style={{ fontSize: '0.9rem', lineHeight: '1.4', marginTop: '8px', marginBottom: 0 }}>
              <strong>Hinweis zur verantwortlichen Stelle</strong><br/>
              Die verantwortliche Stelle für die Datenverarbeitung auf dieser Website ist im Impressum zu finden. Verantwortliche Stelle ist die natürliche oder juristische Person, die allein oder gemeinsam mit anderen über die Zwecke und Mittel der Verarbeitung von personenbezogenen Daten (z. B. Namen, E-Mail-Adressen o. Ä.) entscheidet.
            </p>
            <p style={{ fontSize: '0.9rem', lineHeight: '1.4', marginTop: '8px', marginBottom: 0 }}>
              <strong>Widerruf Ihrer Einwilligung zur Datenverarbeitung</strong><br/>
              Viele Datenverarbeitungsvorgänge sind nur mit Ihrer ausdrücklichen Einwilligung möglich. Sie können eine bereits erteilte Einwilligung jederzeit widerrufen. Dazu reicht eine formlose Mitteilung per E-Mail an uns.
            </p>
            <p style={{ fontSize: '0.9rem', lineHeight: '1.4', marginTop: '8px', marginBottom: 0 }}>
              <strong>Beschwerderecht bei der zuständigen Aufsichtsbehörde</strong><br/>
              Im Falle von Verstößen gegen die DSGVO steht dem Betroffenen ein Beschwerderecht bei einer Aufsichtsbehörde zu.
            </p>
            <p style={{ fontSize: '0.9rem', lineHeight: '1.4', marginTop: '8px', marginBottom: 0 }}>
              <strong>Recht auf Datenübertragbarkeit</strong><br/>
              Sie haben das Recht, Daten, die wir auf Grundlage Ihrer Einwilligung oder in Erfüllung eines Vertrags automatisiert verarbeiten, an sich oder an einen Dritten in einem gängigen, maschinenlesbaren Format aushändigen zu lassen.
            </p>
            <p style={{ fontSize: '0.9rem', lineHeight: '1.4', marginTop: '8px', marginBottom: 0 }}>
              <strong>SSL- bzw. TLS-Verschlüsselung</strong><br/>
              Diese Seite nutzt aus Sicherheitsgründen und zum Schutz der Übertragung vertraulicher Inhalte eine SSL- bzw. TLS-Verschlüsselung.
            </p>
        </AnimatedCard>

        <AnimatedCard style={getCardStyle()} delay={0.4}>
            <h2 style={{ color: headingColor, fontSize: '1.1rem', marginTop: 0 }}>4. Datenerfassung auf dieser Website</h2>
            <p style={{ fontSize: '0.9rem', lineHeight: '1.4', margin: 0 }}>
              <strong>Cookies</strong><br/>
              Unsere Internetseiten verwenden so genannte „Cookies“. Cookies sind kleine Textdateien und richten auf Ihrem Endgerät keinen Schaden an. Sie dienen dazu, unser Angebot nutzerfreundlicher, effektiver und sicherer zu machen.
            </p>
            <p style={{ fontSize: '0.9rem', lineHeight: '1.4', marginTop: '8px', marginBottom: 0 }}>
              <strong>Server-Log-Dateien</strong><br/>
              Der Provider der Seiten erhebt und speichert automatisch Informationen in so genannten Server-Log-Dateien, die Ihr Browser automatisch an uns übermittelt (z.B. Browsertyp, Uhrzeit der Serveranfrage, IP-Adresse).
            </p>
        </AnimatedCard>

        <AnimatedCard style={getCardStyle()} delay={0.5}>
            <h2 style={{ color: headingColor, fontSize: '1.1rem', marginTop: 0 }}>5. Plugins und Tools</h2>
            <p style={{ fontSize: '0.9rem', lineHeight: '1.4', margin: 0 }}>
              <strong>Firebase Authentication</strong><br/>
              Wir nutzen Firebase Authentication, um Anmeldungen und Authentifizierungen zu verwalten. Anbieter ist Google Ireland Limited. Hierbei werden Daten wie E-Mail-Adressen und Passwörter (verschlüsselt) oder OAuth-Token verarbeitet.
            </p>
            <p style={{ fontSize: '0.9rem', lineHeight: '1.4', marginTop: '8px', marginBottom: 0 }}>
              <strong>Google Firestore</strong><br/>
              Wir nutzen Google Firestore als Datenbank. Hierbei werden Ihre Nutzerdaten (z.B. Lernfortschritt, XP, Einstellungen) auf Servern von Google gespeichert, um Ihnen den Dienst bereitzustellen.
            </p>
        </AnimatedCard>

        <AnimatedCard style={getCardStyle()} delay={0.6}>
            <h2 style={{ color: headingColor, fontSize: '1.1rem', marginTop: 0 }}>6. Konto- und Datenlöschung</h2>
            <p style={{ fontSize: '0.9rem', lineHeight: '1.4', margin: 0 }}>
              <strong>Löschung deiner Daten beantragen</strong><br/>
              Wenn du dein Duocode-Konto und alle damit verbundenen persönlichen Daten (wie Lernfortschritt, XP und Zertifikate) unwiderruflich löschen lassen möchtest, sende uns bitte eine formlose E-Mail mit dem Betreff "Konto löschen" an <strong>info@duocode.fun</strong>.<br/>
              Wir werden dein Konto und alle zugehörigen Daten anschließend umgehend aus unserer Datenbank entfernen.
            </p>
        </AnimatedCard>
      </div>
    </main>
  );
}

export default Privacy;
